import React, { useState } from 'react';
import { connect } from 'react-redux';

import TreeView from 'components/common/treeview';
import {
  onStatementNavSectionSelect,
  setHighlightedElementIdContentPanel,
  deallocateItems,
} from 'store/actions/statement-content-actions';
import { setBlacklineSelectedElementId } from 'store/actions/blackline-view-actions';
import { scrollElementIntoViewAlertOnMissing } from 'utils/scrolling-utils';
import { toast } from 'react-toastify';
import { FormattedMessage } from 'react-intl';
import { syncScrollIntoView } from 'utils/sync-scroll-utils';

const CompareDiffComponent = ({
  zoom,
  diffData,
  onSectionClick,
  setHighlightedElementIdContentPanel,
  setBlacklineSelectedElementId,
  deallocateItems,
  selectedStatement,
  blacklineViewSyncScrollingMode,
  isOCR,
  sectionsInView,
  revision,
  sectionIdList,
  syncScrollIds,
  blacklineViewMode,
}) => {
  const [selectedRow, setSelectedRow] = useState(null);

  return (
    <div className="diff-list">
      {Object.keys(diffData).map((sectionName, index) => {
        const sectionData = diffData[sectionName];

        const addedSections = sectionData['0'];
        const changedSections = sectionData['3'];
        const deletedSections = sectionData['4'];

        return (
          <div className="diff-info" key={sectionName}>
            <div className="section-name">{sectionName}</div>
            <div className="section-content">
              <ul>
                {addedSections && (
                  <CollapsibleRow
                    zoom={zoom}
                    index={index}
                    rowTitle="Added"
                    sectionInfo={addedSections}
                    onSectionClick={onSectionClick}
                    setHighlightedElementIdContentPanel={
                      setHighlightedElementIdContentPanel
                    }
                    setBlacklineSelectedElementId={
                      setBlacklineSelectedElementId
                    }
                    deallocateItems={deallocateItems}
                    selectedStatement={selectedStatement}
                    blacklineViewSyncScrollingMode={
                      blacklineViewSyncScrollingMode
                    }
                    isOCR={isOCR}
                    sectionsInView={sectionsInView}
                    revision={revision}
                    sectionIdList={sectionIdList}
                    syncScrollIds={syncScrollIds}
                    blacklineViewMode={blacklineViewMode}
                    selectedRow={selectedRow}
                    setSelectedRow={setSelectedRow}
                  />
                )}
                {changedSections && (
                  <CollapsibleRow
                    zoom={zoom}
                    index={index}
                    rowTitle="Changed"
                    sectionInfo={changedSections}
                    onSectionClick={onSectionClick}
                    setHighlightedElementIdContentPanel={
                      setHighlightedElementIdContentPanel
                    }
                    setBlacklineSelectedElementId={
                      setBlacklineSelectedElementId
                    }
                    deallocateItems={deallocateItems}
                    selectedStatement={selectedStatement}
                    blacklineViewSyncScrollingMode={
                      blacklineViewSyncScrollingMode
                    }
                    isOCR={isOCR}
                    sectionsInView={sectionsInView}
                    revision={revision}
                    sectionIdList={sectionIdList}
                    syncScrollIds={syncScrollIds}
                    blacklineViewMode={blacklineViewMode}
                    selectedRow={selectedRow}
                    setSelectedRow={setSelectedRow}
                  />
                )}
                {deletedSections && (
                  <CollapsibleRow
                    zoom={zoom}
                    index={index}
                    rowTitle="Deleted"
                    sectionInfo={deletedSections}
                    onSectionClick={onSectionClick}
                    clickable={sectionName.toLowerCase() !== 'heading removed'}
                    setHighlightedElementIdContentPanel={
                      setHighlightedElementIdContentPanel
                    }
                    setBlacklineSelectedElementId={
                      setBlacklineSelectedElementId
                    }
                    deallocateItems={deallocateItems}
                    selectedStatement={selectedStatement}
                    blacklineViewSyncScrollingMode={
                      blacklineViewSyncScrollingMode
                    }
                    isOCR={isOCR}
                    sectionsInView={sectionsInView}
                    revision={revision}
                    sectionIdList={sectionIdList}
                    syncScrollIds={syncScrollIds}
                    blacklineViewMode={blacklineViewMode}
                    selectedRow={selectedRow}
                    setSelectedRow={setSelectedRow}
                  />
                )}
              </ul>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const CollapsibleRow = ({
  rowTitle,
  sectionInfo,
  clickable = true,
  setHighlightedElementIdContentPanel,
  setBlacklineSelectedElementId,
  left,
  zoom,
  deallocateItems,
  selectedStatement,
  blacklineViewSyncScrollingMode,
  sectionsInView,
  revision,
  index,
  sectionIdList,
  syncScrollIds,
  blacklineViewMode,
  selectedRow,
  setSelectedRow,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isOCR = selectedStatement.isOCR;
  const dpi = selectedStatement.dpi;

  return (
    <li>
      <TreeView
        label={`${rowTitle} (${sectionInfo.length})`}
        expanded={expanded}
        onSelect={() => setExpanded(!expanded)}
        toggle={() => setExpanded(!expanded)}
        panel={left}
        index={index}
      >
        <ul className="row-items">
          {sectionInfo.map((item) => {
            return clickable ? (
              <li
                key={item.id}
                className={`clickable-element-title ${
                  selectedRow === item.id
                    ? 'clickable-element-title--selected'
                    : null
                }`}
                onClick={() => {
                  if (isOCR) {
                    setBlacklineSelectedElementId({
                      id: item.id,
                      type: rowTitle,
                    });
                    syncScrollIntoView({
                      isSyncScrollingMode: blacklineViewSyncScrollingMode,
                      rowTitle,
                      elementDetails: item,
                      zoom,
                      dpi,
                      sectionIdList,
                      syncScrollIds,
                      blacklineViewMode,
                    });
                  } else {
                    setHighlightedElementIdContentPanel(item.id);
                    scrollElementIntoViewAlertOnMissing({
                      elementId: item.id,
                      sectionId: item.sectionId,
                      rowTitle,
                      missingElementCallback: () =>
                        toast.warning(
                          <FormattedMessage id="compare-panel-element-deleted-notification" />,
                          item.id,
                          item.sectionId,
                        ),
                      isOCR: isOCR,
                      sectionsInView: sectionsInView,
                      revision: revision,
                    });
                  }
                  deallocateItems();
                  setSelectedRow(item.id);
                }}
              >
                {item.display}
              </li>
            ) : (
              <li className="element-title">{item.display}</li>
            );
          })}
        </ul>
      </TreeView>
    </li>
  );
};

const mapStateToProps = ({
  data: {
    selectedStatement,
    revision,
    statementContent: { sectionIdList },
  },
  ui: {
    statementPage: {
      syncScrollIds,
      panels: { left },
      modes: { blacklineViewSyncScrollingMode, blacklineViewMode },
      zoom,
      sectionsInView,
    },
  },
}) => ({
  left,
  zoom,
  selectedStatement,
  blacklineViewSyncScrollingMode,
  sectionsInView,
  revision,
  sectionIdList,
  syncScrollIds,
  blacklineViewMode,
});

const mapDispatchToProps = {
  onSectionClick: onStatementNavSectionSelect,
  setHighlightedElementIdContentPanel,
  setBlacklineSelectedElementId,
  deallocateItems,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CompareDiffComponent);
